import { Dialog, DialogContent, Grid, TextField } from "@mui/material";
import React, { useCallback, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import { AlertLineURLDTO } from "../../models/alert-line-url/alert-line-url-dto";
import { AlertLineURLViolationClassificationRef } from "../../models/violation-classification/alert-line-url-violation-classification-ref";
import { ViolationClassificationDTO } from "../../models/violation-classification/violation-classification-dto";
import AddEditButton from "../../reusable-components/AddEditButton/AddEditButton";
import AutocompleteDropdown from "../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { AlertLineURLApiService } from "../../services/alert-line/alert-line-url-api.service";
import { AvailableLanguagesApiService } from "../../services/available-languages/available-languages-api.service";
import { ViolationClassificationApiService } from "../../services/violation-classification/violation-classification-api.service";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../DialogActions/DialogActions";
import DialogTitle from "../DialogTitle/DialogTitle";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

interface Props {
  effect: "edit" | "add";
  asLink?: boolean;
  linkText?: string;
  item?: ViolationClassificationDTO;
  buttonVariant?: "text" | "icon" | "button";
  buttonText?: string;
  handleNotifyParent?: () => void;
}

interface State {
  open: boolean;
  error: string;
  CCDOpen: boolean;
  loading: boolean;
  item: ViolationClassificationDTO;
}

export const AddEditViolationClassification = ({
  effect,
  item,
  handleNotifyParent,
  asLink = false,
  linkText,
  buttonVariant,
  buttonText,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_edit_violation_classification";

  const DEFAULT_VIOLATION_CLASSIFICATION: Partial<ViolationClassificationDTO> = useMemo(
    () => ({
      id: "",
      isDeleted: false,
      name: "",
      description: "",
      code: "",
      language: null,
      alertLineURLs: [],
    }),
    []
  );

  const INITIAL_STATE = useMemo(
    () => ({
      open: false,
      error: "",
      CCDOpen: false,
      loading: false,
      item: item ?? (DEFAULT_VIOLATION_CLASSIFICATION as ViolationClassificationDTO),
    }),
    [item, DEFAULT_VIOLATION_CLASSIFICATION]
  );

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  //#region handle change
  const handleChange = useCallback(
    (field: keyof ViolationClassificationDTO, value: ViolationClassificationDTO[keyof ViolationClassificationDTO]) => {
      setState((state) => ({
        ...state,
        item: {
          ...state.item,
          [field]: value,
        },
      }));
    },
    []
  );
  //#endregion

  //#region change orders
  const handleChangeAlertLineURLs = useCallback(
    (alertLineURLs: AlertLineURLDTO[]) => {
      const existingalertLineURLs = state.item.alertLineURLs.map((_alertLineURLVC) => _alertLineURLVC.alertLineURL?.id);

      setState((state) => ({
        ...state,
        item: {
          ...state.item,
          alertLineURLs: [
            ...alertLineURLs.map(
              (_alertLineURL) =>
                ({
                  id: existingalertLineURLs.includes(_alertLineURL.id)
                    ? state.item.alertLineURLs.find(
                        (_alertLineURLVC) => _alertLineURLVC.alertLineURL?.id === _alertLineURL.id
                      )?.id ?? ""
                    : uuid(),
                  alertLineURL: _alertLineURL,
                  isDeleted: false,
                  violationClassification: state.item,
                } as AlertLineURLViolationClassificationRef)
            ),
          ],
        },
      }));
    },
    [state.item.alertLineURLs]
  );
  //#endregion

  //#region save
  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new ViolationClassificationApiService().saveItems([
        {
          ...state.item,
          ...(effect === "add" && {
            id: uuid(),
          }),
        },
      ]);
      if (!response.error) {
        setState(INITIAL_STATE);
        handleNotifyParent && handleNotifyParent();
      } else {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error,
      }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [INITIAL_STATE, handleNotifyParent, state.item, effect]);
  //#endregion

  return (
    <>
      <AddEditButton
        effect={effect}
        title={`${t(effect === "add" ? `${tRP}.add_title` : `${tRP}.edit_title`)}`}
        onClick={() => setState((state) => ({ ...state, open: true }))}
        asLink={asLink}
        linkText={linkText}
        {...(buttonVariant === "text" && {
          buttonText: buttonText ? buttonText : effect === "edit" ? "Edit" : `${tRP}.edit_title`,
        })}
      />

      <Dialog
        open={state.open}
        fullWidth
      >
        <DialogTitle
          title={effect === "add" ? `${t(`${tRP}.add_title`)}` : `${t(`${tRP}.edit_title`)}`}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent>
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            <Grid style={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "10px" }}>
              <TextField
                value={state.item.name}
                label={`${t("common_words.name")}`}
                onChange={(e) => handleChange("name", e.currentTarget.value)}
                style={{ width: "100%" }}
              />
              <TextField
                value={state.item.code}
                label={`${t("common_words.code")}`}
                onChange={(e) => handleChange("code", e.currentTarget.value)}
                style={{ width: "100%" }}
              />
              <TextField
                value={state.item.description}
                label={`${t("common_words.description")}`}
                onChange={(e) => handleChange("description", e.currentTarget.value)}
                style={{ width: "100%" }}
              />
              <AutocompleteDropdown
                labelKey={`${t(`${tRP}.fields.alert_line_urls`)}`}
                selectedValue={state.item.alertLineURLs.map((alertLineURLVC) => alertLineURLVC.alertLineURL)}
                serviceApi={new AlertLineURLApiService()}
                field={"alertLineURLs"}
                fieldProp="url"
                handleChange={(field, value) => handleChangeAlertLineURLs(value)}
                isMultiSelect
              />
              <AutocompleteDropdown
                labelKey={`${t(`${tRP}.fields.language`)}`}
                selectedValue={state.item.language}
                serviceApi={new AvailableLanguagesApiService()}
                field={"language"}
                handleChange={handleChange}
              />
            </Grid>
          </LoadingOverlay>
        </DialogContent>

        <DialogActions
          handleSave={handleSave}
          handleCancel={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() => setState(INITIAL_STATE)}
      />
    </>
  );
};
